import { InformationFilled } from '@carbon/icons-react';
import * as React from 'react';
import {
  Accordion,
  Body,
  Heading,
  Input,
  Loading,
  StaticBadge,
  SystemMessage,
} from 'vet-bones/design-system/components';

import { PanelProps } from 'src/components/panelStack/PanelStack';
import { PanelWithSidebar } from 'src/components/resourceDialog/PanelWithSidebar';
import { ProjectFileUploadOptionsForm } from 'src/components/resourceDialog/ProjectFileUploadOptionsForm';
import { ResourcePreviewTable } from 'src/components/resourceDialog/ResourcePreviewTable';
import { MAX_CSV_VISIBLE_LINES } from 'src/constants/limits';
import {
  TEST_IDS,
  getProjectFileUploadPanelColumnLabelTestId,
} from 'src/constants/testIds';
import { useProjectResourceDialog } from 'src/containers/ProjectNewResourceDialogV2/context/useProjectResourceDialog';
import {
  ProjectFileUploadOptions,
  defaultProjectFileUploadOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { FileCreationMetadata } from 'src/types/fileUpload';
import { doesFileHaveDuplicateHeaders } from 'src/utils/csv/doesFileHaveDuplicateHeaders';
import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';
import { useProjectFileUploadParser } from 'src/utils/hooks/useProjectFileUploadParser';

import styles from 'src/components/resourceDialog/styles/resourceDisplayPanel.module.scss';
import tableStyles from 'src/components/resourceDialog/styles/resourcePreviewTable.module.scss';

export type FileUploadPanelProps = {
  fileIndex: number;
};

export const FileUploadPanel: React.VFC<PanelProps<FileUploadPanelProps>> = ({
  closePanel,
  fileIndex,
}) => {
  const {
    selectedFiles,
    fileMetadata,
    setFileMetadata,
    setFooterOptions,
    setFullSize,
    setDialogTitle,
    setHeaderBackButton,
  } = useProjectResourceDialog();

  const [isPropertiesOpen, setIsPropertiesOpen] = React.useState(true);
  const currentFile = selectedFiles[fileIndex];
  const currentMetadata = fileMetadata[fileIndex];

  const [fileName, setFileName] = React.useState(
    currentMetadata?.fileName || currentFile?.name.replace(/\.csv$/i, '') || ''
  );
  const [fileOptions, setFileOptions] =
    React.useState<ProjectFileUploadOptions>(
      currentMetadata?.fileOptions || defaultProjectFileUploadOptions
    );

  const initialFields = React.useRef(currentMetadata?.fields);

  React.useEffect(() => {
    setFileName(currentMetadata?.fileName || '');
    setFileOptions(
      currentMetadata?.fileOptions || defaultProjectFileUploadOptions
    );
    initialFields.current = currentMetadata?.fields;
  }, [fileIndex, currentFile, currentMetadata]);

  const [hasDuplicateHeaders, setHasDuplicateHeaders] = React.useState(false);
  const didFileChange = useDidValueChange(currentFile);
  const didFileOptionsChange = useDidValueChange(fileOptions);

  React.useEffect(() => {
    if (!currentFile) {
      return;
    }

    if (didFileChange || didFileOptionsChange) {
      doesFileHaveDuplicateHeaders(fileOptions)(currentFile).then(
        (hasDupes) => {
          setHasDuplicateHeaders(hasDupes);
        }
      );
    }
  }, [currentFile, didFileChange, didFileOptionsChange, fileOptions]);

  const didHasDuplicateHeadersChange = useDidValueChange(hasDuplicateHeaders);

  React.useEffect(() => {
    if (didHasDuplicateHeadersChange && hasDuplicateHeaders) {
      setFileOptions((prev) => ({
        ...prev,
        header: false,
      }));
    }
  }, [didHasDuplicateHeadersChange, hasDuplicateHeaders]);

  const { errors, fields, isParsing, rows, updateFieldLabel } =
    useProjectFileUploadParser(
      currentFile,
      fileOptions,
      MAX_CSV_VISIBLE_LINES,
      initialFields.current
    );

  const latestValues = React.useRef({ fileName, fileOptions, fields, rows });
  React.useEffect(() => {
    latestValues.current = { fileName, fileOptions, fields, rows };
  }, [fileName, fileOptions, fields, rows]);

  const saveToMetadata = React.useCallback(() => {
    if (!currentFile) {
      return;
    }
    const { fileName, fileOptions, fields, rows } = latestValues.current;
    setFileMetadata((prev: Record<number, FileCreationMetadata>) => ({
      ...prev,
      [fileIndex]: {
        fields,
        rowCount: rows.length,
        fileName,
        fileOptions,
      },
    }));
  }, [setFileMetadata, fileIndex, currentFile]);

  React.useEffect(() => {
    if (!isParsing && currentFile) {
      saveToMetadata();
    }
  }, [
    fileName,
    fileOptions,
    fields,
    rows.length,
    isParsing,
    saveToMetadata,
    currentFile,
  ]);

  React.useEffect(() => {
    const capturedFileIndex = fileIndex;
    const capturedFile = currentFile;
    return () => {
      if (!capturedFile) {
        return;
      }
      const { fileName, fileOptions, fields, rows } = latestValues.current;
      setFileMetadata((prev) => ({
        ...prev,
        [capturedFileIndex]: {
          fields,
          rowCount: rows.length,
          fileName,
          fileOptions,
        },
      }));
    };
  }, [fileIndex, setFileMetadata, currentFile]);

  const handleBack = React.useCallback(() => {
    // Auto-fix fixable errors by applying default values
    const needsFix =
      !fileOptions.separator || !fileOptions.quote || !fileOptions.escape;

    if (needsFix) {
      const fixedFileOptions: ProjectFileUploadOptions = {
        ...fileOptions,
        separator:
          fileOptions.separator || defaultProjectFileUploadOptions.separator,
        quote: fileOptions.quote || defaultProjectFileUploadOptions.quote,
        escape: fileOptions.escape || defaultProjectFileUploadOptions.escape,
      };

      setFileOptions(fixedFileOptions);

      latestValues.current = {
        ...latestValues.current,
        fileOptions: fixedFileOptions,
      };

      if (currentFile) {
        const { fileName, fields, rows } = latestValues.current;
        setFileMetadata((prev: Record<number, FileCreationMetadata>) => ({
          ...prev,
          [fileIndex]: {
            fields,
            rowCount: rows.length,
            fileName,
            fileOptions: fixedFileOptions,
          },
        }));
      }
    }

    setFullSize(false);
    if (closePanel) {
      closePanel();
    }
  }, [
    setFullSize,
    closePanel,
    fileOptions,
    setFileOptions,
    currentFile,
    fileIndex,
    setFileMetadata,
  ]);

  React.useEffect(() => {
    setFullSize(true);
    setDialogTitle(
      copy.components.projectResourceDialog.fileUploadPanelV2.title
    );
    setHeaderBackButton(() => handleBack);
    setFooterOptions({});

    return () => {
      setHeaderBackButton(null);
      setFullSize(false);
    };
  }, [
    setFullSize,
    setDialogTitle,
    setHeaderBackButton,
    handleBack,
    setFooterOptions,
  ]);

  const hasErrors = errors.length > 0;
  const visibleRows = React.useMemo(
    () => rows.slice(0, MAX_CSV_VISIBLE_LINES),
    [rows]
  );

  const preview = React.useMemo(() => {
    if (isParsing) {
      return (
        <div className={styles.previewLoading}>
          <Loading size="lg" />
        </div>
      );
    }
    if (hasErrors) {
      return (
        <div>
          <SystemMessage
            text={
              <>
                <Heading size="md">
                  {
                    copy.components.projectResourceDialog.projectFileUploadPanel
                      .errors.title
                  }
                </Heading>
                {errors.map((error) => (
                  <div key={`error-${error.row}-${error.message}`}>
                    {
                      copy.components.projectResourceDialog
                        .projectFileUploadPanel.errors.row
                    }{' '}
                    {error.row}: {error.message}
                  </div>
                ))}
              </>
            }
            variant="alert"
          />
        </div>
      );
    }
    if (!fileOptions.separator || !fileOptions.quote || !fileOptions.escape) {
      return (
        <SystemMessage
          text={
            copy.components.projectResourceDialog.fileUploadPanelV2
              .fillAllProperties
          }
          variant="alert"
        />
      );
    }
    if (rows.length === 0) {
      return (
        <SystemMessage
          text={
            copy.components.projectResourceDialog.projectFileUploadPanel.errors
              .empty
          }
          variant="info"
        />
      );
    }
    const lastModifiedDate = new Date(currentFile?.lastModified);
    const month = String(lastModifiedDate.getMonth() + 1).padStart(2, '0');
    const day = String(lastModifiedDate.getDate()).padStart(2, '0');
    const year = lastModifiedDate.getFullYear();
    const lastModified = `${month}.${day}.${year}`;

    return (
      <>
        <div className={styles.fileInfo}>
          <div className={styles.headerRow}>
            <Input
              className={styles.fileName}
              clearable={false}
              id="file-name-input"
              label=""
              minimal
              onChange={(e) => {
                setFileName(e.target.value);
              }}
              placeholder={
                copy.components.projectResourceDialog.fileUploadPanelV2
                  .fileNamePlaceholder
              }
              size="lg-inside"
              value={fileName}
            />
            <div className={styles.headerRight}>
              {lastModified && (
                <div className={styles.lastUpdated}>
                  {copy.components.projectResourceDialog.fileUploadPanelV2.lastUpdated(
                    lastModified
                  )}
                  {rows.length >= MAX_CSV_VISIBLE_LINES && '   /'}
                </div>
              )}
              {rows.length >= MAX_CSV_VISIBLE_LINES && (
                <StaticBadge
                  leftIcon={InformationFilled}
                  size="md"
                  type="info"
                  variant="muted"
                >
                  {
                    copy.components.projectResourceDialog.fileUploadPanelV2
                      .mayNotReflectFullData
                  }
                </StaticBadge>
              )}
            </div>
          </div>
        </div>
        <ResourcePreviewTable
          fields={fields}
          headerTestIdFn={getProjectFileUploadPanelColumnLabelTestId}
          onFieldLabelChange={updateFieldLabel}
          renderHeaderExtra={(field) => (
            <div className={tableStyles.statistics}>
              <Body ellipsize size="sm">
                {copy.components.projectResourceDialog.projectFileUploadPanel.statistics.distinct(
                  field.statistics.distinct
                )}
              </Body>
              <Body ellipsize size="sm">
                {copy.components.projectResourceDialog.projectFileUploadPanel.statistics.records(
                  field.statistics.records
                )}
              </Body>
            </div>
          )}
          rows={visibleRows}
          testId={TEST_IDS.projectFileUploadPanel.table}
        />
      </>
    );
  }, [
    isParsing,
    hasErrors,
    errors,
    fileOptions.separator,
    fileOptions.quote,
    fileOptions.escape,
    rows.length,
    currentFile?.lastModified,
    fileName,
    visibleRows,
    fields,
    updateFieldLabel,
  ]);

  if (!currentFile) {
    return (
      <div className={styles.fallbackContainer}>
        <SystemMessage
          text={
            copy.components.projectResourceDialog.fileUploadPanelV2.fileNotFound
          }
          variant="info"
        />
      </div>
    );
  }

  const asideHeader = (
    <Input
      clearable={false}
      disabled
      label={copy.components.projectResourceDialog.fileUploadPanelV2.fileInput}
      size="md"
      value={currentFile?.name || ''}
    />
  );

  const asideContent = (
    <Accordion
      icon="left"
      isOpen={isPropertiesOpen}
      label={
        copy.components.projectResourceDialog.fileUploadPanelV2
          .resourceProperties
      }
      onInteraction={setIsPropertiesOpen}
      size="md"
    >
      <ProjectFileUploadOptionsForm
        disableHeaderToggle={hasDuplicateHeaders}
        fileOptions={fileOptions}
        onSetFileOptions={setFileOptions}
      />
    </Accordion>
  );

  return (
    <PanelWithSidebar
      asideContent={asideContent}
      asideHeader={asideHeader}
      mainContent={preview}
    />
  );
};
